<template>
  <teleport to="body">
    <div v-if="modelValue">
      <div class="lx-drawer-scrim" @click="close" aria-hidden="true"></div>
      <aside class="lx-drawer-panel" role="dialog" aria-modal="true" aria-labelledby="mtf-drawer-title" ref="panel" @keydown.esc="close">
        <div class="lx-drawer-panel__head">
          <div>
            <span class="kicker">Reference</span>
            <h2 id="mtf-drawer-title">Methodology &amp; how to use</h2>
          </div>
          <button type="button" class="lx-close" aria-label="Close" ref="closeBtn" @click="close">×</button>
        </div>
        <div class="lx-drawer-panel__tabs">
          <div class="lx-seg" role="tablist" aria-label="Methodology sections">
            <button v-for="t in tabs" :key="t.id" type="button" role="tab" class="lx-seg__btn" :class="{ 'lx-seg__btn--on': tab === t.id }" :aria-selected="tab === t.id" :id="'mtf-tab-' + t.id" @click="tab = t.id">{{ t.label }}</button>
          </div>
        </div>

        <div class="lx-drawer-panel__body" role="tabpanel" :aria-labelledby="'mtf-tab-' + tab">
          <!-- HOW TO USE: the original instructions, restructured -->
          <template v-if="tab === 'use'">
            <p>
              MTF converts quarterly macroeconomic forecasts into weekly market forecasts along a 52-week trajectory.
              The forecastable market variables are the S&amp;P500 (SPX), the most representative stock market index in each
              country (BOLSA), the nominal exchange rate of the local currency with respect to the USD (NER), and the 5-year
              sovereign credit default swap premium (CDS).
            </p>
            <h3>A. Generate Consensus Janus Forecasts</h3>
            <ol>
              <li>Choose a country. The table shows consensus quarterly forecasts for the macro variables the H2F2 model uses as inputs (end-of-quarter figures; GDP growth is a quarterly average).</li>
              <li>Click <b>Apply</b> to see the weekly forecasts of CDS, NER and BOLSA associated with them. This assumes SPX is exogenous. To endogenize it, switch <b>Endogenize SPX</b> on and click <b>Apply</b> again.</li>
              <li>Choose a variable under <b>Chart</b> to compare the Consensus Janus Forecast with the Consensus Market one (no market consensus exists for CDS).</li>
            </ol>
            <h3>B. Generate Alternative Janus Forecasts</h3>
            <ol>
              <li>Edit any macro assumption in the input table. Modified cells are highlighted and show the consensus value on hover; <b>reset</b> restores a row, <b>Reset all</b> restores the table.</li>
              <li>Click <b>Apply</b>. The output shows alternative forecasts consistent with the modified scenario, the scenario tag changes to <i>Alternative</i>, and the table gains a <i>Δ vs Janus baseline</i> row.</li>
              <li>Compare Consensus and Alternative Janus forecasts with the Market consensus under <b>Chart</b>. Download the numbers with <b>Export</b>.</li>
            </ol>
            <h3>Reading the uncertainty</h3>
            <p>
              Shaded bands are <b>prediction intervals</b> for the forecast path at each horizon (pointwise, not a simultaneous
              guarantee). The label under the chart states the method and sample used. When no interval is available the bands are absent and the reason is stated; a missing band is never a zero-width band.
            </p>
            <p class="lx-legal">
              Important: H2F2 is a proprietary product of Janus4cast LLC for which a USPTO patent is pending. Reproducing
              graphical and/or numerical results obtained by using this tool requires citing www.janus4cast.com as the primary source.
            </p>
          </template>

          <!-- MODEL CARD -->
          <template v-else-if="tab === 'model'">
            <h3>Model card</h3>
            <dl class="lx-dl">
              <dt>Target</dt><dd>Weekly levels of CDS (bp), NER (local currency per USD), BOLSA (index) and, when endogenous, SPX, over 52 weeks.</dd>
              <dt>Inputs</dt><dd>Quarterly consensus for local GDP growth, inflation, policy rate and 10-year rate; US GDP, inflation, policy and 10-year rates; China GDP; S&amp;P 500; VIX; EURUSD, GBPUSD, USDJPY; oil, gas, aluminium, copper, corn, soybeans. Interpolated to weekly paths.</dd>
              <dt>Structure</dt><dd>Recursive one-step system: SPGSCI → DXY → (SPX) → CDS → NER → BOLSA. Each equation is a country-specific regression in levels, first differences or error-correction form, chosen out of sample.</dd>
              <dt>Estimator</dt><dd>{{ card.modelVariant || "not provided" }}</dd>
              <dt>Version</dt><dd>{{ card.modelVersion || "not provided" }}</dd>
              <dt>Estimated</dt><dd>{{ card.modelEstimatedAt || "not provided" }}</dd>
              <dt>Data as of</dt><dd>{{ card.dataAsOf || "not provided" }}</dd>
              <dt>Re-estimation</dt><dd>Weekly re-estimation and monthly re-selection are scheduled in the data pipeline; the version string above is the authoritative record.</dd>
            </dl>
            <h3 v-if="equations.length">Equations in this country</h3>
            <table v-if="equations.length" class="lx-mtable">
              <thead><tr><th scope="col">Series</th><th scope="col">Spec</th><th scope="col">Estimator</th><th scope="col" class="num">Regressors</th><th scope="col">Sample</th></tr></thead>
              <tbody>
                <tr v-for="e in equations" :key="e.series">
                  <td>{{ e.series }}</td><td>{{ e.spec }}</td><td>{{ e.estimator }}</td>
                  <td class="num">{{ e.nSelected != null ? e.nSelected : (e.coefficients ? e.coefficients.length : "—") }}</td>
                  <td>{{ e.sampleStart || "—" }} → {{ e.sampleEnd || "latest" }}</td>
                </tr>
              </tbody>
            </table>
            <h3>Known limitations</h3>
            <ul>
              <li v-for="(l, i) in limitationsShown" :key="i">{{ l }}</li>
            </ul>
          </template>

          <!-- DATA SOURCES -->
          <template v-else-if="tab === 'data'">
            <h3>Series and sources</h3>
            <p>Weekly Friday observations. The forecast origin is the last Friday with a complete market edge (NER and SPX).</p>
            <ForecastDataLineage :lineage="lineage" />
            <h3>Market consensus</h3>
            <p>Trading Economics forecast paths, interpolated weekly and re-anchored to the platform's last observation. Not a survey consensus. No consensus exists for CDS.</p>
          </template>

          <!-- GLOSSARY -->
          <template v-else>
            <dl class="lx-dl">
              <dt>Janus baseline</dt><dd>The forecast produced from the market-consensus inputs.</dd>
              <dt>Alternative scenario</dt><dd>The forecast produced after you edit one or more inputs.</dd>
              <dt>Market consensus</dt><dd>Trading Economics forecast path for the same variable.</dd>
              <dt>Forecast origin</dt><dd>Last observed date; everything to its right is projected.</dd>
              <dt>Prediction interval</dt><dd>Range expected to contain the realised value at that horizon with the stated probability. Pointwise, not simultaneous. Different from a confidence interval on a coefficient.</dd>
              <dt>RMSE / MAE</dt><dd>Root mean squared error / mean absolute error of forecasts against realised values.</dd>
              <dt>Bias</dt><dd>Mean of forecast minus realised; positive = the model ran high.</dd>
              <dt>Theil's U</dt><dd>RMSE of the model divided by RMSE of a no-change forecast from the origin. Below 1 beats the no-change benchmark.</dd>
              <dt>Hit rate</dt><dd>Share of horizons where the forecast's direction from the origin matched the realised direction (mean directional accuracy vs anchor).</dd>
              <dt>Coverage</dt><dd>Share of realised values that fell inside the stated interval. Should be close to the nominal level; lower means the model is over-confident.</dd>
              <dt>pp</dt><dd>Percentage points: the unit of a change in a rate or growth figure.</dd>
              <dt>Scenario hash</dt><dd>Deterministic fingerprint of country, SPX mode, inputs and model version. Identifies a scenario; reproduces it only if the model version can be retrieved.</dd>
            </dl>
          </template>
        </div>
      </aside>
    </div>
  </teleport>
</template>

<script>
import ForecastDataLineage from "./ForecastDataLineage.vue";

export default {
  name: "ForecastMethodologyDrawer",
  components: { ForecastDataLineage },
  props: {
    modelValue: { type: Boolean, default: false },
    card: { type: Object, default: () => ({}) }, // {modelVariant, modelVersion, modelEstimatedAt, dataAsOf}
    equations: { type: Array, default: () => [] },
    lineage: { type: Array, default: () => [] },
    limitations: { type: Array, default: () => [] },
    initialTab: { type: String, default: "use" },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      tab: this.initialTab,
      tabs: [
        { id: "use", label: "How to use" },
        { id: "model", label: "Model" },
        { id: "data", label: "Data sources" },
        { id: "glossary", label: "Glossary" },
      ],
      prevFocus: null,
    };
  },
  computed: {
    limitationsShown() {
      if (this.limitations && this.limitations.length) return this.limitations;
      return [
        "Quarterly inputs are linearly interpolated to weekly paths; the slope changes at quarter joins and the recursion amplifies it (the first join falls around week 10).",
        "The recursive one-step process produces genuine week-to-week variation in the first weeks even with smooth inputs; a guardrail caps week-over-week moves.",
      ];
    },
  },
  watch: {
    modelValue(open) {
      if (open) {
        this.prevFocus = document.activeElement;
        document.documentElement.style.overflow = "hidden";
        this.$nextTick(() => this.$refs.closeBtn && this.$refs.closeBtn.focus());
      } else {
        document.documentElement.style.overflow = "";
        if (this.prevFocus && this.prevFocus.focus) this.prevFocus.focus();
      }
    },
  },
  beforeUnmount() {
    document.documentElement.style.overflow = "";
  },
  methods: {
    close() {
      this.$emit("update:modelValue", false);
    },
  },
};
</script>
